import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { BrandMark } from "@/components/ui/brand-mark";
import { MarketingLocaleSwitcher } from "@/features/marketing/locale-switcher";

/**
 * Split layout shared by login, forgot-password and reset-password: product
 * hero on the left, form on the right (same composition as the console entry).
 */
export function AuthBrandLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation("pages");

  return (
    <div className="flex min-h-dvh flex-col md:flex-row">
      <aside className="login-brand-panel relative flex flex-col justify-between overflow-hidden px-6 py-8 md:w-[42%] md:px-10 md:py-12">
        <img
          src="/marketing/hero-lab-storage.jpg"
          alt=""
          className="login-brand-panel-photo absolute inset-0 h-full w-full object-cover object-center"
          width={1920}
          height={1080}
          decoding="async"
        />
        <div className="login-brand-panel-scrim absolute inset-0" aria-hidden="true" />
        <div className="relative z-10 flex items-start justify-between gap-4">
          <BrandMark size="lg" withKicker tone="onDark" />
          <MarketingLocaleSwitcher className="shrink-0" tone="onDark" />
        </div>
        <div className="relative z-10 mt-10 max-w-sm md:mt-0">
          <p className="text-base text-white md:text-lg">{t("loginPanelLead")}</p>
          <p className="mt-3 text-sm text-white/75">{t("loginTrustNote")}</p>
        </div>
      </aside>

      <main className="flex flex-1 items-center justify-center bg-surface px-4 py-10 md:px-10">
        <div className="w-full max-w-sm">
          <div className="mb-6">
            <h1 className="text-xl font-semibold tracking-tight text-ink">{title}</h1>
            {subtitle ? <p className="mt-1 text-sm text-ink-muted">{subtitle}</p> : null}
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
